// src/lib/api/utilities/jobData.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { logger } from '../../logger'

/**
 * Get job checklists (utility version)
 * Migrated from supabaseApi.js lines 400-420
 */
const _getJobChecklists = async (companyId = null) => {
  logger.utilities('getJobChecklists called with companyId:', companyId)
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    console.warn('[utilities] No company ID found')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('job_checklists')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .order('created_at', { ascending: false })
    
  if (error) {
    console.error('[utilities] Error fetching job checklists:', error)
    throw error
  }

  logger.utilities('getJobChecklists success, found', data?.length || 0, 'checklists')
  return createSuccessResponse(data || [])
}

/**
 * Get job checklist items (utility version)
 * Migrated from supabaseApi.js lines 421-441
 */
const _getJobChecklistItems = async (companyId = null) => {
  logger.utilities('getJobChecklistItems called with companyId:', companyId)
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    console.warn('[utilities] No company ID found')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('job_checklist_items')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .order('position', { ascending: true })
    
  if (error) {
    console.error('[utilities] Error fetching job checklist items:', error)
    throw error
  }

  logger.utilities('getJobChecklistItems success, found', data?.length || 0, 'items')
  return createSuccessResponse(data || [])
}

/**
 * Get job activity (utility version)
 * Migrated from supabaseApi.js lines 1488-1530
 */
const _getJobActivity = async (companyId = null) => {
  logger.utilities('getJobActivity called with companyId:', companyId)
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    console.warn('[utilities] No company ID found')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('job_activity')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .order('created_at', { ascending: false })
    
  if (error) {
    console.error('[utilities] Error fetching job activity:', error)
    throw error
  }

  logger.utilities('getJobActivity success, found', data?.length || 0, 'activity items')
  return createSuccessResponse(data || [])
}

/**
 * Get job media (utility version)
 * Migrated from supabaseApi.js lines 442-462
 */
const _getJobMedia = async (companyId = null) => {
  logger.utilities('getJobMedia called with companyId:', companyId)
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    console.warn('[utilities] No company ID found')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('job_media')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .order('created_at', { ascending: false })
    
  if (error) {
    console.error('[utilities] Error fetching job media:', error)
    throw error
  }

  logger.utilities('getJobMedia success, found', data?.length || 0, 'media items')
  return createSuccessResponse(data || [])
}

/**
 * Get job checklist attachments (utility version)
 * Migrated from supabaseApi.js lines 463-484
 */
const _getJobChecklistAttachments = async (companyId = null) => {
  logger.utilities('getJobChecklistAttachments called with companyId:', companyId)
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    console.warn('[utilities] No company ID found')
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('job_checklist_attachments')
    .select('*')
    .eq('company_id', resolvedCompanyId)
    .order('created_at', { ascending: false })
    
  if (error) {
    console.error('[utilities] Error fetching job checklist attachments:', error)
    throw error
  }

  logger.utilities('getJobChecklistAttachments success, found', data?.length || 0, 'attachments')
  return createSuccessResponse(data || [])
}

// Export functions with error handling
export const getJobChecklists = withErrorHandling(_getJobChecklists, 'get job checklists', 'job_checklists')
export const getJobChecklistItems = withErrorHandling(_getJobChecklistItems, 'get job checklist items', 'job_checklist_items')
export const getJobActivity = withErrorHandling(_getJobActivity, 'get job activity', 'job_activity')
export const getJobMedia = withErrorHandling(_getJobMedia, 'get job media', 'job_media')
export const getJobChecklistAttachments = withErrorHandling(_getJobChecklistAttachments, 'get job checklist attachments', 'job_checklist_attachments')